<script lang="ts" setup>
import { computed } from 'vue'

const props = withDefaults(defineProps<{
  modelValue: number
  id: string
  hideControlsMobile: boolean
  minVal: number
  maxVal: number
  stepper?: number
  readonlyInput?: boolean
}>(), {
  stepper: 1,
  readonlyInput: false
})

const input = computed({
  get() {
    return props.modelValue
  },
  set(value: number) {
    if (value > props.maxVal) {
      value = props.maxVal
    } else if (value < props.minVal) {
      value = props.minVal
    }
    emit('update:modelValue', value)
  }
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: number): void
}>()

function increaseClicked(): void {
  const newVal = props.modelValue + props.stepper
  input.value = newVal > props.maxVal ? props.maxVal : newVal
}

function decreaseClicked(): void {
  const newVal = props.modelValue - props.stepper
  input.value = newVal < props.minVal ? props.minVal : newVal
}

function onInputChanged(e: Event) {
  const inputElement = e.target as HTMLInputElement
  const rawVal = inputElement.value.trim()

  if (isNaN(Number(rawVal))) {
    input.value = props.minVal
    inputElement.value = String(props.minVal)
    return
  }

  let num = parseInt(rawVal, 10)

  if (num > props.maxVal) num = props.maxVal
  if (num < props.minVal) num = props.minVal

  input.value = num
  inputElement.value = String(num)
}

function onInputBlur(e: Event) {
  const inputElement = e.target as HTMLInputElement
  const rawVal = inputElement.value.trim()

  if (rawVal === '' || isNaN(Number(rawVal))) {
    input.value = props.minVal
    inputElement.value = String(props.minVal)
  }
}

function preventNonNumericInput(e: Event) {
  const inputEvent = e as InputEvent
  const data = inputEvent.data

  if (data && !/^\d+$/.test(data)) {
    e.preventDefault()
  }
}

</script>

<template>
  <div class="spinner">
    <button
      :class="{ 'hide-mobile': hideControlsMobile }"
      :disabled="input <= minVal"
      class="btn spinner__button is--decrease"
      type="button"
      @click.stop="decreaseClicked"
    >
      <i class="ic-minus" />
    </button>

    <input
      :id="id"
      v-model="input"
      :class="{ active: input > 0 }"
      :max="maxVal"
      :min="minVal"
      class="spinner__input"
      type="number"
      :readonly="readonlyInput"
      :disabled="readonlyInput"
      @input="onInputChanged"
      @beforeinput="preventNonNumericInput"
      @blur="onInputBlur"
      @click.stop
    >

    <button
      :class="{ 'hide-mobile': hideControlsMobile }"
      :disabled="input >= maxVal"
      class="btn spinner__button is--increase"
      type="button"
      @click.stop="increaseClicked"
    >
      <i class="ic-plus" />
    </button>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/style/helpers/include-media';

.spinner {
  display: flex;
  align-items: center;

  &__input {
    -webkit-appearance: none;
    -moz-appearance: textfield;
    padding: 0;
    text-align: center;
    height: rem-calc(52);
    width: rem-calc(52);
    margin-right: rem-calc(8);

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    &.active {
      @include themify($themes) {
        border-color: themed('primaryColor');
        background-color: themed('primaryColorLight');
      }
    }

    @include themify($themes) {
      border-radius: rem-calc(themed('inputBorderRadius'));
    }

    @include media('<desktop') {
      width: rem-calc(102);
    }
  }

  &__button {
    min-width: auto;
    height: rem-calc($inputHeight);
    width: rem-calc($inputHeight);
    padding: 0;
    position: relative;
    margin-right: rem-calc(8);
    transition: background-color 0.1s;

    &:last-child {
      margin-right: 0;
    }

    i {
      font-size: rem-calc(29);
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate3d(-50%, -50%, 0);
      transition: color 0.1s;
    }

    @include themify($themes) {
      background-color: #fff;
      border-radius: rem-calc(themed('inputBorderRadius'));
      border-color: themed('primaryColor');
      color: themed('h1Color');

      &:hover {
        border-color: themed('primaryColor');
        background-color: themed('primaryColor');
        color: #fff;
      }
    }

    &:disabled,
    &:disabled:hover {
      opacity: 1;
      background-color: #fff;
      border-color: $borderColor;
      color: $borderColor;
    }
  }
}
</style>
